import React, { useState } from "react";
import {
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Button,
  Typography,
  Stepper,
  Step,
  StepLabel,
  Box,
  TextField,
  Alert,
  CircularProgress,
  useTheme,
} from "@mui/material";
import { useMutation } from "@apollo/client";
import { START_ASSESSMENT } from "../../../../../../apis/organization-apis/risk-apis/riskApis";

const steps = ["Confirmation", "Assessment Details"];

export default function ConfirmStartAssessmentDialog({
  open,
  onClose,
  setSnackbarInfo,
}) {
  const [activeStep, setActiveStep] = useState(0);
  const [title, setTitle] = useState("");
  const [description, setDescription] = useState("");
  const [error, setError] = useState("");
  const [startAssessment, { loading: startAssessmentLoading }] =
    useMutation(START_ASSESSMENT);
  const theme = useTheme();

  const handleStartAssessment = async (details) => {
    try {
      const { data } = await startAssessment({
        variables: {
          assessment_data: {
            title: details?.title,
            description: details?.description,
          },
        },
      });
      if (data?.startRiskAssessment?.success) {
        setSnackbarInfo({
          message: "Assessment started successfully!",
          vertical: "top",
          horizontal: "right",
        });
        handleClose(true);
      } else {
        setSnackbarInfo({
          message:
            data?.startRiskAssessment?.message || "Failed to start assessment",
          vertical: "top",
          horizontal: "right",
        });
      }
    } catch (err) {
      setSnackbarInfo({
        message: `Error: ${err.message}`,
        vertical: "top",
        horizontal: "right",
      });
    }
  };

  const handleNext = () => {
    if (activeStep === 0) {
      setActiveStep(1);
    } else if (activeStep === 1) {
      if (!title.trim()) {
        setError("Title is required.");
        return;
      }
      setError("");
      handleStartAssessment({ title, description });
    }
  };

  const handleBack = () => {
    if (activeStep === 1) {
      setActiveStep(0);
      setError("");
    } else {
      onClose(false);
    }
  };

  const handleClose = (success) => {
    setActiveStep(0);
    setTitle("");
    setDescription("");
    setError("");
    onClose(success);
  };

  return (
    <Dialog
      open={open}
      onClose={() => handleClose(false)}
      maxWidth="sm"
      fullWidth
      sx={{
        "& .MuiDialog-paper": {
          borderRadius: 2,
          boxShadow: theme.customColors.shadow.main,
        },
      }}
    >
      <DialogTitle
        sx={{
          fontWeight: 600,
          color: theme.customColors.text.strong,
          borderBottom: `1px solid ${theme.customColors.border.subtle}`,
        }}
      >
        Start Assessment
      </DialogTitle>
      <DialogContent>
        <Stepper
          activeStep={activeStep}
          sx={{
            m: 2,
            "& .MuiStepLabel-root .Mui-completed": {
              color: theme.customColors.status.success.main,
            },
            "& .MuiStepLabel-root .Mui-active": {
              color: theme.customColors.button.dark,
            },
            "& .MuiStepLabel-label": {
              fontSize: "13px",
              fontWeight: 600,
            },
          }}
        >
          {steps.map((label) => (
            <Step key={label}>
              <StepLabel>{label}</StepLabel>
            </Step>
          ))}
        </Stepper>
        {activeStep === 0 && (
          <Box>
            <Alert
              severity="info"
              sx={{
                mb: 2,
                backgroundColor: theme.customColors.background.info,
                border: `1px solid ${theme.customColors.border.accent}`,
              }}
            >
              <Typography
                variant="body1"
                sx={{
                  fontWeight: 500,
                  mb: 1,
                  color: theme.customColors.text.strong,
                }}
              >
                You are about to start an assessment.
              </Typography>
              <Typography
                variant="body2"
                sx={{
                  mb: 1,
                  color: theme.customColors.text.secondary,
                }}
              >
                This assessment will include:
              </Typography>
              <Box component="ul" sx={{ pl: 2, m: 0 }}>
                <Typography
                  component="li"
                  variant="body2"
                  sx={{ color: theme.customColors.text.secondary }}
                >
                  Current risk details
                </Typography>
                <Typography
                  component="li"
                  variant="body2"
                  sx={{ color: theme.customColors.text.secondary }}
                >
                  Inherent and residual risk assessments
                </Typography>
                <Typography
                  component="li"
                  variant="body2"
                  sx={{ color: theme.customColors.text.secondary }}
                >
                  Control effectiveness data
                </Typography>
                <Typography
                  component="li"
                  variant="body2"
                  sx={{ color: theme.customColors.text.secondary }}
                >
                  Risk categorization and ownership information
                </Typography>
              </Box>
              <Typography
                variant="body2"
                sx={{
                  mt: 1,
                  fontStyle: "italic",
                  color: theme.customColors.text.muted,
                }}
              >
                The assessment will be created in a pending state and can be
                reviewed before final approval.
              </Typography>
            </Alert>
          </Box>
        )}
        {activeStep === 1 && (
          <Box>
            <TextField
              label="Assessment Title"
              value={title}
              onChange={(e) => setTitle(e.target.value)}
              fullWidth
              required
              sx={{ mb: 2 }}
            />
            <TextField
              label="Assessment Description"
              value={description}
              onChange={(e) => setDescription(e.target.value)}
              fullWidth
              multiline
              minRows={2}
            />
            {error && (
              <Alert
                severity="error"
                sx={{
                  mt: 2,
                  backgroundColor: theme.customColors.status.error.bg,
                  border: `1px solid ${theme.customColors.status.error.border}`,
                  color: theme.customColors.status.error.text,
                }}
              >
                {error}
              </Alert>
            )}
          </Box>
        )}
      </DialogContent>
      <DialogActions>
        <Button
          onClick={handleBack}
          color="inherit"
          sx={{
            color: theme.customColors.text.secondary,
            "&:hover": {
              backgroundColor: theme.customColors.background.hover,
            },
          }}
        >
          {activeStep === 0 ? "Cancel" : "Back"}
        </Button>
        <Button
          onClick={handleNext}
          variant="contained"
          color="primary"
          disabled={startAssessmentLoading}
          sx={{
            fontWeight: 500,
            borderRadius: "8px",
            textTransform: "none",
            fontSize: "14px",
            backgroundColor: theme.customColors.button.dark,
            color: theme.customColors.button.text,
            "&:hover": {
              fontWeight: 500,
              backgroundColor: theme.customColors.button.hover,
              color: theme.customColors.button.text,
            },
            "&:disabled": {
              fontWeight: 500,
              backgroundColor: theme.customColors.border.disabled,
              color: theme.customColors.text.disabled,
            },
          }}
        >
          {activeStep === 0 ? "Next" : "Start Assessment"}
          {startAssessmentLoading && (
            <CircularProgress
              size={20}
              sx={{ color: theme.customColors.text.disabled, ml: 1 }}
            />
          )}
        </Button>
      </DialogActions>
    </Dialog>
  );
}
